import React, {useCallback, useEffect, useMemo} from 'react';
import {browserHistory} from 'react-router';
import LibraryActions from 'actions/library/LibraryActions';
import {LIBRARY_ROUTE, libraryLinkName} from '@store/library/library.consts';
import Breadcrumbs from 'components/pure/utils/Breadcrumbs';
import ConstValues from 'utils/const';
import LibrarySearchInfo from './LibrarySearchInfo';
import CallsListComponent from '@components/calls-list/CallsListComponent';

interface Props {
  // Router params
  params: any;

  // methods
  clearSearchInfo: () => void;
  fetchLibrariesData: () => Promise<void>;
  searchCallsDefer: () => void;
  updateLibraryDropdown: (libraryId: number | 'any') => void;
  updateLibrarySearchType: (searchType: string) => void;
  updateSearchingCalls: (searchCalls: boolean) => void;

  // from global state
  searchCalls: boolean;
  searchInfo;
  searchType: string;
  libraryId: number;
  libraryTagToDelete;

  // perhaps useless, but need to check nested component CallsListComponent
  createForm;
  user;
  shareAccessible;
  sources;
  formState;
}

export const LibrarySearchResults: React.FC<Props> = ({
  // Router params
  params,

  // methods
  clearSearchInfo,
  fetchLibrariesData,
  searchCallsDefer,
  updateLibraryDropdown,
  updateLibrarySearchType,
  updateSearchingCalls,

  libraryTagToDelete,
  searchCalls,
  searchInfo,
  searchType,
  user,
  ...props
}) => {
  useEffect(() => {
    setTimeout(fetchLibrariesData, 0); // avoid dispatch in dispatch exception here.
    return () => {
      updateSearchingCalls(false);
      clearSearchInfo();
      updateLibraryDropdown('any');
    };
  }, [clearSearchInfo, fetchLibrariesData, updateLibraryDropdown, updateSearchingCalls]);

  // @NOTE: libraryId must always be number on UI (API works with string and number)
  const libraryId = useMemo(() => {
    if (params.libraryId) {
      const paramsLibIdNum = parseInt(params.libraryId, 10);
      if (!isNaN(paramsLibIdNum)) {
        return paramsLibIdNum;
      }
    }

    return props.libraryId;
  }, [params.libraryId, props.libraryId]);

  useEffect(() => {
    if (libraryId) {
      updateLibraryDropdown(libraryId);
      updateLibrarySearchType(ConstValues.librarySearchType.browse);
      searchCallsDefer();
      browserHistory.replace(LIBRARY_ROUTE.SEARCH_LIBRARY(libraryId));
    }
  }, [searchCalls, libraryId, searchCallsDefer, updateLibraryDropdown, updateLibrarySearchType]);

  const onClickBreadcrumbsLink = useCallback(
    linkName => {
      if (linkName === libraryLinkName(user)) {
        LibraryActions.updateSearchingCalls(false);
        setImmediate(() => LibraryActions.reinitLibrarySearchContext());
        setImmediate(() => LibraryActions.clearCallersAutocomplete());
      }
    },
    [user]
  );

  const links = useMemo<Array<{name: string; url: string}>>(
    () => [
      {name: libraryLinkName(user), url: '/library'},
      {name: searchType, url: window.location.pathname},
    ],
    [searchType, user]
  );

  return (
    <div className="library-search-results-container">
      <div className="header">
        <Breadcrumbs links={links} onClickLink={onClickBreadcrumbsLink} />
        <LibrarySearchInfo searchInfo={searchInfo} />
      </div>
      <CallsListComponent {...props} isLibrarySearch={true} libraryId={libraryId} />
    </div>
  );
};
